/** @format */
import { Injectable } from '@angular/core';
import {
    CompositeRule,
    CourseUnit,
    CourseUnitSubstitution,
    CustomCourseUnitAttainment,
    CustomModuleAttainment,
    CustomStudyDraft,
    EntityWithRule,
    GradeRaiseAttempt,
    LocalId,
    Module,
    OtmId,
} from 'common-typescript/types';
import { Subject } from 'rxjs';
import { PlanActionsService, PlanOperation, PlanOperationType, UiOperation, UiOperationType } from 'sis-components';

@Injectable()
export class StudentPlanActionsService implements PlanActionsService {
    planOperationSubject: Subject<PlanOperation>;
    uiOperationSubject: Subject<UiOperation>;

    constructor() {
        this.planOperationSubject = new Subject<PlanOperation>();
        this.uiOperationSubject = new Subject<UiOperation>();
    }

    /*
     * UI-operations
     */

    openCourseUnitInfoModal(courseUnit: CourseUnit): void {
        this.uiOperationSubject.next({
            uiOperationType: UiOperationType.OPEN_COURSE_UNIT_INFO_MODAL,
            target: courseUnit,
        });
    }

    openCustomCourseUnitAttainmentInfoModal(customCourseUnitAttainment: CustomCourseUnitAttainment): void {
        this.uiOperationSubject.next({
            uiOperationType: UiOperationType.OPEN_CUSTOM_ATTAINMENT_INFO_MODAL,
            target: customCourseUnitAttainment,
        });
    }

    openCustomStudyDraftCreationModal(parentModule: EntityWithRule): void {
        this.uiOperationSubject.next({
            uiOperationType: UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_CREATION_MODAL,
            target: parentModule,
        });
    }

    openCustomStudyDraftInfoModal(customStudyDraft: CustomStudyDraft): void {
        this.uiOperationSubject.next({
            uiOperationType: UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_INFO_MODAL,
            target: customStudyDraft,
        });
    }

    openModule(module: EntityWithRule): void {
        this.uiOperationSubject.next({
            uiOperationType: UiOperationType.OPEN_MODULE,
            target: module,
        });
    }

    openModuleInfoModal(module: EntityWithRule): void {
        this.uiOperationSubject.next({
            uiOperationType: UiOperationType.OPEN_MODULE_INFO_MODAL,
            target: module,
        });
    }

    openSelectCompletionMethodModal(courseUnit: CourseUnit): void {
        this.uiOperationSubject.next({
            uiOperationType: UiOperationType.OPEN_SELECT_COMPLETION_METHOD_MODAL,
            target: courseUnit,
        });
    }

    openAttainmentDetailsModal(courseUnit: CourseUnit): void {
        this.uiOperationSubject.next({
            uiOperationType: UiOperationType.OPEN_ATTAINMENT_DETAILS_MODAL,
            target: courseUnit,
        });
    }

    openSelectCompletionMethodWithGradeRaiseAttemptModal(courseUnit: CourseUnit): void {
        this.uiOperationSubject.next({
            uiOperationType: UiOperationType.OPEN_SELECT_COMPLETION_METHOD_WITH_GRADE_RAISE_ATTEMPT_MODAL,
            target: courseUnit,
        });
    }

    /*
     *  Plan-operations
     */

    selectCourseUnit(courseUnit: CourseUnit, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.SELECT_COURSE_UNIT,
            target: courseUnit,
            parentModule,
        });
    }

    selectCourseUnitByGroupId(courseUnitGroupId: OtmId, parentModule: EntityWithRule) {
        this.planOperationSubject.next(<PlanOperation>{
            parentModule,
            planOperationType: PlanOperationType.SELECT_COURSE_UNIT_BY_GROUP_ID,
            target: courseUnitGroupId,
        });
    }

    removeCourseUnit(courseUnit: CourseUnit, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.UNSELECT_COURSE_UNIT,
            target: courseUnit,
            parentModule,
        });
    }

    moveCourseUnit(courseUnit: CourseUnit, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.MOVE_COURSE_UNIT,
            target: courseUnit,
            parentModule,
            parentCourseUnit: null,
        });
    }

    selectModule(module: Module, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.SELECT_MODULE,
            target: module,
            parentModule,
            parentCourseUnit: null,
        });
    }

    removeModule(module: Module, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.UNSELECT_MODULE,
            target: module,
            parentModule,
            parentCourseUnit: null,
        });
    }

    selectModuleByGroupId(moduleGroupId: OtmId, parentModule: EntityWithRule) {
        this.planOperationSubject.next({
            parentModule,
            planOperationType: PlanOperationType.SELECT_MODULE_BY_GROUP_ID,
            target: moduleGroupId,
            parentCourseUnit: null,
        });
    }

    activateRuleGroup(rule: CompositeRule, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.ACTIVATE_RULE_GROUP,
            target: rule,
            parentModule,
        });
    }

    deactivateRuleGroup(rule: CompositeRule, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.DEACTIVATE_RULE_GROUP,
            target: rule,
            parentModule,
        });
    }

    forceRemoveCourseUnit(courseUnit: CourseUnit, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.FORCE_REMOVE_COURSE_UNIT,
            target: courseUnit,
            parentModule,
        });
    }

    forceSelectCourseUnitById(courseUnitId: string, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.FORCE_SELECT_COURSE_UNIT,
            target: courseUnitId,
            parentModule,
        });
    }

    forceRemoveModule(module: Module, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.FORCE_REMOVE_MODULE,
            target: module,
            parentModule,
            parentCourseUnit: null,
        });
    }

    forceSelectModuleById(moduleId: string, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.FORCE_SELECT_MODULE,
            target: moduleId,
            parentModule,
            parentCourseUnit: null,
        });
    }

    selectCustomCourseUnitAttainment(customCourseUnitAttainment: CustomCourseUnitAttainment, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.FORCE_SELECT_CUSTOM_COURSE_UNIT_ATTAINMENT,
            target: customCourseUnitAttainment,
            parentModule,
        });
    }

    removeCustomCourseUnitAttainmentById(customCourseUnitAttainmentId: OtmId, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.UNSELECT_CUSTOM_COURSE_UNIT_ATTAINMENT_BY_ID,
            target: customCourseUnitAttainmentId,
            parentModule,
        });
    }

    selectCustomModuleAttainment(customModuleAttainment: CustomModuleAttainment, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.FORCE_SELECT_CUSTOM_MODULE_ATTAINMENT,
            target: customModuleAttainment,
            parentModule,
        });
    }

    removeCustomModuleAttainmentById(customModuleAttainmentId: OtmId, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.UNSELECT_CUSTOM_MODULE_ATTAINMENT_BY_ID,
            target: customModuleAttainmentId,
            parentModule,
        });
    }

    addCustomStudyDraft(customStudyDraft: CustomStudyDraft, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.ADD_CUSTOM_STUDY_DRAFT,
            target: customStudyDraft,
            parentModule,
        });
    }

    changeCourseUnitVersion(oldCourseUnitVersion: CourseUnit, newCourseUnitVersion: CourseUnit) {
        this.planOperationSubject.next({
            oldCourseUnitVersion,
            newCourseUnitVersion,
            parentModule: null,
            planOperationType: PlanOperationType.CHANGE_COURSE_UNIT_VERSION,
            target: null,
            parentCourseUnit: null,
        });
    }

    removeCustomStudyDraft(customStudyDraft: CustomStudyDraft, parentModule: EntityWithRule): void {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.UNSELECT_CUSTOM_STUDY_DRAFT_BY_ID,
            target: customStudyDraft.id,
            parentModule,
        });
    }

    selectCompletionMethod(courseUnit: CourseUnit, completionMethodId: LocalId) {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.SELECT_COMPLETION_METHOD,
            target: completionMethodId,
            parentModule: null,
            parentCourseUnit: courseUnit,
        });
    }

    addAssessmentItemSelection(courseUnit: CourseUnit, assessmentItemId: OtmId) {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.ADD_ASSESSMENT_ITEM_SELECTION,
            target: assessmentItemId,
            parentModule: null,
            parentCourseUnit: courseUnit,
        });
    }

    removeAssessmentItemSelection(courseUnit: CourseUnit, assessmentItemId: OtmId) {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.REMOVE_ASSESSMENT_ITEM_SELECTION,
            target: assessmentItemId,
            parentModule: null,
            parentCourseUnit: courseUnit,
        });
    }

    removeGradeRaiseAttempt(courseUnitId: OtmId) {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.REMOVE_GRADE_RAISE_ATTEMPT,
            target: courseUnitId,
            parentModule: null,
        });
    }

    addGradeRaiseAttempt(courseUnitId: OtmId, gradeRaiseAttempt: GradeRaiseAttempt) {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.ADD_GRADE_RAISE_ATTEMPT,
            target: courseUnitId,
            parentModule: null,
            gradeRaiseAttempt,
        });
    }

    selectSubstitutions(
        courseUnit: CourseUnit,
        substitutions: CourseUnitSubstitution[],
        substituteCourseUnitsByGroupId: { [key: OtmId]: CourseUnit },
    ) {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.SELECT_SUBSTITUTIONS,
            target: courseUnit,
            substitutions,
            substituteCourseUnitsByGroupId,
        });
    }

    removeSubstitutions(courseUnit: CourseUnit) {
        this.planOperationSubject.next(<PlanOperation>{
            planOperationType: PlanOperationType.REMOVE_SUBSTITUTION,
            target: courseUnit,
        });
    }
}
